import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { db } from '../lib/db';

// ERxModal
// Opens the active e-prescribing vendor's UI in a full-screen iframe overlay.
// Vendor-agnostic: the SSO URL is built by the caller via src/lib/erx.js
// (getErxVendor().buildSsoUrl), so this modal renders any vendor's iframe.
//
// Props:
//   patientId    — local patients.id
//   providerId   — local provider identifier (for audit/display)
//   ssoUrl       — complete vendor SSO iframe URL (built via src/lib/erx.js)
//   vendorLabel  — display name, e.g. 'DoseSpot' | 'MDToolbox-Rx'
//   onClose(status) — called on dismiss; status = 'sent'|'pending'|'error'|null

export default function ERxModal({
  patientId,
  providerId,
  ssoUrl,
  vendorLabel = 'e-Prescribing',
  onClose,
}) {
  const [loading, setLoading] = useState(true);
  const openedAt = useRef(Date.now());

  // ESC key closes modal
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') handleClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []); // eslint-disable-line

  async function handleClose() {
    // Query erx_transmissions for the most recent row written since the modal
    // opened — RLS scopes results to the authenticated practice automatically.
    let status = null;
    try {
      const since = new Date(openedAt.current).toISOString();
      const { data } = await db
        .from('erx_transmissions')
        .select('status')
        .gte('created_at', since)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (data?.status) status = data.status;
    } catch (_) {
      // non-fatal
    }
    onClose(status);
  }

  const modal = (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`${vendorLabel} e-Prescribing`}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        background: 'rgba(0,0,0,0.75)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}
    >
      <div
        style={{
          background: '#1d2f40',
          borderRadius: 12,
          border: '1px solid rgba(255,255,255,0.1)',
          width: 'min(94vw, 1100px)',
          height: 'min(90vh, 820px)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          boxShadow: '0 32px 96px rgba(0,0,0,0.7)',
          position: 'relative',
        }}
      >
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '11px 18px',
          borderBottom: '1px solid rgba(255,255,255,0.08)',
          flexShrink: 0,
          background: '#162436',
        }}>
          <span style={{ fontWeight: 600, fontSize: 14, color: '#e8edf2' }}>
            💊 e-Prescribing — {vendorLabel}
          </span>
          <button
            onClick={handleClose}
            aria-label="Close e-Rx"
            style={{
              background: 'none',
              border: 'none',
              color: '#8fa3b4',
              fontSize: 20,
              cursor: 'pointer',
              lineHeight: 1,
              padding: '2px 6px',
              borderRadius: 4,
            }}
          >
            ✕
          </button>
        </div>

        {/* Loading spinner */}
        {loading && (
          <div style={{
            position: 'absolute',
            inset: 0,
            top: 44, // below header
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            background: '#1d2f40',
            zIndex: 1,
            gap: 12,
          }}>
            <div style={{
              width: 36,
              height: 36,
              border: '3px solid rgba(255,255,255,0.1)',
              borderTop: '3px solid #0ea5a0',
              borderRadius: '50%',
              animation: 'erx-spin 0.75s linear infinite',
            }} />
            <span style={{ color: '#8fa3b4', fontSize: 13 }}>Loading {vendorLabel}…</span>
            <style>{`@keyframes erx-spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        )}

        {/* Iframe */}
        {ssoUrl ? (
          <iframe
            src={ssoUrl}
            title={`${vendorLabel} e-Prescribing`}
            onLoad={() => setLoading(false)}
            style={{
              flex: 1,
              border: 'none',
              width: '100%',
              display: loading ? 'none' : 'block',
            }}
            allow="clipboard-read; clipboard-write"
          />
        ) : (
          <div style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#e05252',
            fontSize: 13,
            padding: 24,
            textAlign: 'center',
          }}>
            Unable to load {vendorLabel} — vendor not configured. Check e-Rx credentials in Secret Manager.
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}
